// 📄 src/utils/pdfUtils.js
import html2pdf from "html2pdf.js";

export const generatePDFBlob = async ({
  language = "en",
  doctors,
  month,
  days,
  staff,
  numDays,
  selectedCategory,
}) => {
  // 🔹 Urdu translation map
  const dutyMapUrdu = {
    M: "صبح",
    E: "دوپہر",
    N: "رات",
    "D/O": "چھٹی",
    P: "حاضر",
    A: "غیر حاضر",
    "N/O": "رات آف",
    "E/O": "دوپہر آف",
    Ramzan: "رمضان",
    "24 Hrs Duty": "چوبیس گھنٹے ڈیوٹی",
    "Eid Duty": "عید ڈیوٹی",
    "M+E": "صبح + دوپہر",
  };

  // 🔹 Helper: Auto Translate to Urdu
  const autoTranslateToUrdu = async (text) => {
    if (!text?.trim()) return text;
    try {
      const res = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ur&dt=t&q=${encodeURIComponent(
          text
        )}`
      );
      const json = await res.json();
      return json[0]?.map((item) => item[0]).join("") || text;
    } catch (err) {
      console.error("Translation error:", err);
      return text;
    }
  };

  // 🧾 Main Logic
  const isUrdu = language === "ur";

  const categoryTitles = {
    SR: isUrdu ? "سینئر رجسٹرار" : "Senior Registrar",
    AP: isUrdu ? "اسسٹنٹ پروفیسر" : "Assistant Professor",
    MO: isUrdu ? "میڈیکل آفیسر" : "Medical Officer",
    PG: isUrdu ? "پوسٹ گریجویٹس" : "Post Graduates",
  };
  const categoryTitle = categoryTitles[selectedCategory] || "";

  const hospitalName = isUrdu
    ? "راولپنڈی انسٹی ٹیوٹ آف کارڈیالوجی"
    : "Rawalpindi Institute of Cardiology";
  const subAddress = isUrdu ? "راول روڈ، راولپنڈی" : "Rawal Road, Rawalpindi";

  const title = categoryTitle
    ? `${categoryTitle} ${isUrdu ? "کی حاضری" : "Attendance"}`
    : isUrdu
    ? "ڈاکٹرز کی حاضری"
    : "Doctors Attendance";

  const leftLogoUrl = "../../assets/img/logo/new-logo.png";
  const rightLogoUrl = "../../assets/img/logo/punjab-logo.png";
  const currentYear = new Date().getFullYear();

  // 🔹 Mark Sundays
  const sundays = [];
  for (let i = 1; i <= numDays; i++) {
    const date = new Date(`${month} ${i}, ${currentYear}`);
    if (date.getDay() === 0) sundays.push(i);
  }

// 🔹 Table Header
const tableHeaderHTML = (() => {
  const dayHeaders = Array.from({ length: numDays }, (_, i) => {
    const dayNum = i + 1;
    const isSunday = sundays.includes(dayNum);
    return `<th style="padding:6px; border:1px solid #ccc; ${
      isSunday
        ? "background:#222; color:#fff; font-weight:700;"
        : "background:#eaf1ff; color:#333;"
    }">${dayNum}</th>`;
  }).join("");

  return `
    <thead>
      <tr>
        <th style="padding:6px; border:1px solid #ccc; background:#0052D4; color:#fff; width:40px;">
          ${isUrdu ? "نمبر شمار" : "Sr#"}
        </th>
        <th style="padding:6px; border:1px solid #ccc; background:#0052D4; color:#fff;">
          ${isUrdu ? "عملے کا نام" : "Name of Doctors"}
        </th>
        ${dayHeaders}
        <th style="padding:6px; border:1px solid #ccc; background:#cfe2ff;">
          ${isUrdu ? "خلاصہ" : "Summary"}
        </th>
      </tr>
    </thead>
  `;
})();

  // 🔹 Build Rows
  const rowsHtml = await Promise.all(
    staff.map(async (personName, index) => {

      const displayName = isUrdu ? await autoTranslateToUrdu(personName) : personName;
      const rowCells = Array.from({ length: numDays }, (_, i) => {
        const dayNum = i + 1;
        const isSunday = sundays.includes(dayNum);
        let value = days[personName]?.[dayNum] ?? "";

        if (isUrdu && dutyMapUrdu[value]) value = dutyMapUrdu[value];

        let displayValue = value;
        if (value === "P") displayValue = `<span style='color:green; font-weight:600;'>P</span>`;
        if (value === "A") displayValue = `<span style='color:red; font-weight:600;'>A</span>`;
        if (value === "L") displayValue = `<span style='color:blue; font-weight:600;'>L</span>`;

        return `<td style="padding:6px; border:1px solid #ccc; ${
          isSunday ? "background:#f5f5f5; font-weight:600;" : ""
        }">${displayValue || "&nbsp;"}</td>`;
      }).join("");

      const attendance = days[personName] || {};
      const values = Object.values(attendance);
      const validDays = values.filter((v) => v !== "D/O");
      const totalP = validDays.filter((v) => v === "P").length;
      const totalA = validDays.filter((v) => v === "A").length;
      const totalL = validDays.filter((v) => v === "L").length;
      const totalCount = validDays.length || 1;
      const percentage = ((totalP / totalCount) * 100).toFixed(1);

      let color = "#dc3545";
      if (percentage >= 80) color = "#28a745";
      else if (percentage >= 60) color = "#ffc107";

      return `
  <tr>
    <td style="padding:6px; border:1px solid #ccc;">${index + 1}</td>
    <td style="padding:6px; border:1px solid #ccc; font-weight:600;">${displayName}</td>
    ${rowCells}
    <td style="padding:6px; border:1px solid #ccc; background:#f0f7ff; font-weight:600; color:${color};">
      <span style="color:green;">P:</span> ${totalP} |
      <span style="color:red;">A:</span> ${totalA} |
      <span style="color:blue;">L:</span> ${totalL} <br>
      (${percentage}%)
    </td>
  </tr>
`;

    })
  );

// 🔹 Paginate Tables (14 rows per page) — header INCLUDED per page (no top-level header)
const chunkSize = 14;
const paginatedTables = [];

for (let i = 0; i < rowsHtml.length; i += chunkSize) {
  const chunk = rowsHtml.slice(i, i + chunkSize).join("");

  // header that will appear on each page
  const pageHeader = `
    <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
      <div style="width:25%;"><img src="${leftLogoUrl}" style="height:50px; object-fit:contain;" /></div>
      <div style="text-align:center; width:60%;">
        <h2 style="margin:0; font-weight:700;">${hospitalName}</h2>
        <p style="margin:2px 0; font-size:11px; color:#555;">${subAddress}</p>
        <h4 style="margin:4px 0; color:#444;">${title} – ${month}</h4>
      </div>
      <div style="width:25%; text-align:${isUrdu ? "left" : "right"};">
        <img src="${rightLogoUrl}" style="height:50px; object-fit:contain;" />
      </div>
    </div>
  `;

  // build single page (header + table)
  const table = `
    <div style="page-break-before:${i === 0 ? "auto" : "always"}; padding-top:6px;">
      ${pageHeader}
      <table style="width:100%; border-collapse:collapse; text-align:center; font-size:9px;">
        ${tableHeaderHTML}
        <tbody>
          ${chunk}
        </tbody>
      </table>
    </div>
  `;

  paginatedTables.push(table);
}

// 🧾 Final PDF Layout — NOTE: no extra header here, only paginatedTables content
const allTablesHTML = paginatedTables.join("");
const content = `
  <div style="font-family:${isUrdu ? "'Noto Nastaliq Urdu'" : "'Segoe UI', sans-serif"};
              direction:${isUrdu ? "rtl" : "ltr"}; padding:12px; font-size:12px; color:#2c3e50;">
    ${allTablesHTML}
    <p style="text-align:center; margin-top:12px; font-size:10px; color:#888;">
      © ${currentYear} ${hospitalName}. ${isUrdu ? "جملہ حقوق محفوظ ہیں۔" : "All rights reserved."}
    </p>
  </div>
`;

  return await html2pdf()
    .from(content)
    .set({
      margin: 0.3,
      filename: `Doctors_Attendance_${month}_${language.toUpperCase()}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
    })
    .output("blob");
};
